/**
 * IncomeModal - Smart income/payment creation modal
 *
 * Features:
 * - Patient selection with yellow warning for unlinked income
 * - Status selection: "ממתין לתשלום" or "תשלום התקבל"
 * - Smart suggestions based on status:
 *   - Pending: suggests completed appointments without payments
 *   - Paid: suggests existing pending payment records
 * - Split payment handling when amounts don't match
 * - Beautiful UI matching AppointmentModal style
 *
 * Error Codes: CK-8830 - CK-8839
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectHasPermission,
  selectIsSecretary,
  selectClinicSharesAllPatients,
} from "../../store/slices/authSlice";
import { useGetPatientsQuery } from "../../store/services/patientsApi";
import {
  useGetAppointmentsWithoutPaymentsQuery,
  useLazyGetPatientPendingDebtsQuery,
  useCreateIncomeWithLinkingMutation,
} from "../../store/services/financeApi";
import { TRANSACTION_STATUS } from "../../firebase";
import {
  X,
  CurrencyCircleDollar,
  User,
  Clock,
  CheckCircle,
  CalendarBlank,
  Warning,
  Info,
  CaretDown,
  Receipt,
  LinkSimple,
  ArrowsClockwise,
  WarningCircle,
  Lightning,
  Money,
  CreditCard,
  Bank,
  FileText,
  DeviceMobile,
  DotsThree,
} from "@phosphor-icons/react";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { HelpButton } from "../shared";
import DatePicker from "../ui/DatePicker";
import ModalCloseButton from "../ui/ModalCloseButton";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import { useToast } from "../../contexts/ToastContext";
import "./IncomeModal.css";
import { getIsraelDateString } from "../../utils/dateUtils";

// Payment methods
const PAYMENT_METHODS = [
  { value: "cash", label: "מזומן", Icon: Money },
  { value: "credit", label: "אשראי", Icon: CreditCard },
  { value: "transfer", label: "העברה", Icon: Bank },
  { value: "check", label: "צ׳ק", Icon: FileText },
  { value: "bit", label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר", Icon: DeviceMobile },
  { value: "other", label: "אחר", Icon: DotsThree },
];

// Status options for income creation
const INCOME_STATUS_OPTIONS = [
  {
    value: TRANSACTION_STATUS.PAID,
    label: "תשלום התקבל",
    description: "הכנסה שכבר התקבלה",
    icon: CheckCircle,
    color: "success",
  },
  {
    value: TRANSACTION_STATUS.PENDING,
    label: "ממתין לתשלום",
    description: "תשלום שעדיין לא התקבל",
    icon: Clock,
    color: "warning",
  },
];

function IncomeModal({ isOpen, onClose, onSuccess, prefilledData = null }) {
  const { showError } = useToast();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  const canCollectForOthers = useSelector(
    selectHasPermission("finance.collectForOthers")
  );
  const isSecretary = useSelector(selectIsSecretary);
  const canViewAllPatients = useSelector(selectHasPermission("patients.viewAllClinic"));
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  // Multi-clinic v2: a user who may collect for colleagues (admin, secretary
  // with finance.collectForOthers) picks from the CLINIC's patients and open
  // appointments, not only their own — a secretary has none of their own.
  // The patients list still needs read scope (admin / viewAllClinic / all-mode).
  const clinicWidePickers =
    isAdmin || (canCollectForOthers === true && (canViewAllPatients === true || sharesAllPatients));

  // Form state
  const [formData, setFormData] = useState({
    status: TRANSACTION_STATUS.PAID,
    amount: "",
    paymentMethod: "cash",
    paymentDate: getIsraelDateString(new Date()),
    description: "",
    notes: "",
    patientId: "",
  });

  // UI state
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [selectedLinkItem, setSelectedLinkItem] = useState(null); // Appointment or pending debt to link
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSplitConfirm, setShowSplitConfirm] = useState(false);

  // Fetch patients
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId: clinicWidePickers ? undefined : therapistId, pageSize: 200 },
    { skip: !clinicId || !therapistId }
  );
  const patients = patientsData?.patients || [];

  // Fetch appointments without payments (for PENDING status)
  const {
    data: appointmentsWithoutPayments = [],
    isLoading: loadingAppointments,
  } = useGetAppointmentsWithoutPaymentsQuery(
    {
      clinicId,
      therapistId,
      patientId: formData.patientId || null,
      limit: 15,
      includeAllTherapists: clinicWidePickers,
    },
    {
      skip:
        !clinicId ||
        !therapistId ||
        formData.status !== TRANSACTION_STATUS.PENDING,
    }
  );

  // Lazy fetch patient pending debts (for PAID status)
  const [
    fetchPatientDebts,
    { data: patientDebts = [], isLoading: loadingDebts },
  ] = useLazyGetPatientPendingDebtsQuery();

  // Mutations
  const [createIncomeWithLinking] = useCreateIncomeWithLinkingMutation();

  // Get selected patient
  const selectedPatient = useMemo(() => {
    return patients.find((p) => p.id === formData.patientId);
  }, [patients, formData.patientId]);

  // Filter patients for dropdown
  const filteredPatients = useMemo(() => {
    const sorted = sortPatientsByName(patients);
    if (!patientSearchQuery) return sorted;
    const query = patientSearchQuery.toLowerCase();
    return sorted.filter(
      (p) =>
        p.firstName?.toLowerCase().includes(query) ||
        p.lastName?.toLowerCase().includes(query) ||
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(query)
    );
  }, [patients, patientSearchQuery]);

  // Effect: Fetch patient debts when patient is selected and status is PAID
  useEffect(() => {
    if (
      formData.patientId &&
      formData.status === TRANSACTION_STATUS.PAID &&
      clinicId &&
      therapistId
    ) {
      fetchPatientDebts({
        clinicId,
        patientId: formData.patientId,
        therapistId: clinicWidePickers ? null : therapistId,
      });
    }
  }, [
    formData.patientId,
    formData.status,
    clinicId,
    therapistId,
    clinicWidePickers,
    fetchPatientDebts,
  ]);

  // Effect: Reset link selection when status changes
  useEffect(() => {
    setSelectedLinkItem(null);
  }, [formData.status]);

  // Effect: Prefill data if provided
  useEffect(() => {
    if (prefilledData) {
      setFormData((prev) => ({
        ...prev,
        ...prefilledData,
      }));
    }
  }, [prefilledData]);

  // Check for amount mismatch when linking
  const amountMismatch = useMemo(() => {
    if (!selectedLinkItem || !formData.amount) return null;

    const paymentAmount = parseFloat(formData.amount) || 0;
    const linkAmount =
      selectedLinkItem.amount || selectedLinkItem.paymentAmount || 0;
    const diff = paymentAmount - linkAmount;

    if (Math.abs(diff) < 0.01) return null;

    return {
      difference: diff,
      isOverpayment: diff > 0,
      isUnderpayment: diff < 0,
      linkAmount,
    };
  }, [formData.amount, selectedLinkItem]);

  // Handlers
  const handlePatientSelect = (patient) => {
    setFormData((prev) => ({ ...prev, patientId: patient.id }));
    setPatientSearchQuery("");
    setShowPatientDropdown(false);
    setSelectedLinkItem(null);
  };

  const handleClearPatient = () => {
    setFormData((prev) => ({ ...prev, patientId: "" }));
    setSelectedLinkItem(null);
  };

  const handleStatusChange = (status) => {
    setFormData((prev) => ({ ...prev, status }));
    setSelectedLinkItem(null);
  };

  const handleLinkItemSelect = (item, type) => {
    if (selectedLinkItem?.id === item.id) {
      // Deselect
      setSelectedLinkItem(null);
    } else {
      setSelectedLinkItem({ ...item, linkType: type });
      // Auto-fill amount from link item
      const itemAmount = item.amount || item.paymentAmount;
      if (itemAmount && !formData.amount) {
        setFormData((prev) => ({ ...prev, amount: itemAmount.toString() }));
      }
    }
  };

  // Spec E7: an income linked to an appointment (or to a colleague's pending
  // debt) is stamped with THAT therapist's id, not the collecting user's.
  const linkedTherapistId = selectedLinkItem?.therapistId || null;
  // A secretary owns no finance: an unlinked income is attributed to the
  // selected patient's primary therapist, never to the secretary (who is
  // hidden from every finance filter/report).
  const secretaryPatientTherapistId = isSecretary ? selectedPatient?.therapistId || null : null;
  const txTherapistId = linkedTherapistId || secretaryPatientTherapistId || therapistId;
  const secretaryNeedsTarget = isSecretary && !linkedTherapistId && !secretaryPatientTherapistId;
  // Non-admins need finance.collectForOthers to record a payment for a
  // colleague's appointment (legacy therapists: false → blocked).
  const blockedForOthers =
    (!!linkedTherapistId &&
      linkedTherapistId !== therapistId &&
      !isAdmin &&
      !canCollectForOthers) ||
    secretaryNeedsTarget;

  const handleSubmit = async () => {
    if (!formData.amount || blockedForOthers) return;

    // Check for split scenario
    if (amountMismatch?.isUnderpayment && selectedLinkItem) {
      setShowSplitConfirm(true);
      return;
    }

    await submitIncome();
  };

  const submitIncome = async (confirmSplit = false) => {
    if (blockedForOthers) return;
    setIsSubmitting(true);
    setShowSplitConfirm(false);

    try {
      const patientName = selectedPatient
        ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
        : null;

      const incomeData = {
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        paymentDate: formData.paymentDate,
        description:
          formData.description ||
          (formData.status === TRANSACTION_STATUS.PENDING
            ? "ממתין לתשלום"
            : "תשלום"),
        notes: formData.notes,
        patientId: formData.patientId || null,
        patientName,
        status: formData.status,
        appointmentId:
          selectedLinkItem?.linkType === "appointment"
            ? selectedLinkItem.id
            : null,
      };

      await createIncomeWithLinking({
        clinicId,
        therapistId: txTherapistId,
        therapistName,
        incomeData,
        linkToTransactionId:
          selectedLinkItem?.linkType === "debt" ? selectedLinkItem.id : null,
      }).unwrap();

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("[CK-8830] Error creating income:", error);
      showError("שגיאה בשמירת ההכנסה. נסו שוב.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format helpers
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return `₪${parseFloat(amount).toLocaleString("he-IL")}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="income-modal-overlay" onClick={onClose}>
      <div className="income-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="income-modal-header">
          <div className="income-modal-title">
            <CurrencyCircleDollar size={24} weight="duotone" />
            <h2>יצירת הכנסה</h2>
          </div>
          <div className="income-modal-header-actions">
            <HelpButton featureKey="income_modal" />
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {/* Body */}
        <div className="income-modal-body">
          {/* Status Selection - Cards Style */}
          <div className="income-section">
            <label className="income-section-label">סוג הרשומה</label>
            <div className="status-cards">
              {INCOME_STATUS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = formData.status === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`status-card ${option.color} ${
                      isSelected ? "selected" : ""
                    }`}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    <Icon size={24} weight={isSelected ? "fill" : "duotone"} />
                    <span className="status-card-label">{option.label}</span>
                    <span className="status-card-desc">
                      {option.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Patient Selection */}
          <div className="income-section">
            <label className="income-section-label">
              שיוך ללקוח
              <span className="optional-badge">אופציונלי</span>
            </label>

            {selectedPatient ? (
              <div className="selected-patient-display">
                <User size={18} weight="fill" />
                <span>
                  {selectedPatient.firstName} {selectedPatient.lastName}
                </span>
                <button
                  type="button"
                  className="clear-patient-btn"
                  onClick={handleClearPatient}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="patient-dropdown-container">
                <button
                  type="button"
                  className="patient-dropdown-trigger"
                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                >
                  <User size={18} weight="light" />
                  <span>בחר לקוח</span>
                  <CaretDown size={16} />
                </button>

                {showPatientDropdown && (
                  <div className="patient-dropdown">
                    <input
                      type="text"
                      placeholder="חפש לקוח..."
                      value={patientSearchQuery}
                      onChange={(e) => setPatientSearchQuery(e.target.value)}
                      className="patient-search-input"
                      autoFocus
                    />
                    <div className="patient-dropdown-list">
                      {filteredPatients.length === 0 ? (
                        <div className="no-patients">לא נמצאו לקוחות</div>
                      ) : (
                        filteredPatients.slice(0, 10).map((patient) => (
                          <button
                            key={patient.id}
                            type="button"
                            className="patient-dropdown-item"
                            onClick={() => handlePatientSelect(patient)}
                          >
                            <User size={16} weight="fill" />
                            <span>
                              {patient.firstName} {patient.lastName}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No patient warning */}
            {!formData.patientId && (
              <div className="no-patient-warning">
                <Warning size={16} weight="fill" />
                <span>הכנסה ללא שיוך ללקוח תסומן בצהוב</span>
              </div>
            )}
          </div>

          {/* Smart Suggestions Section */}
          {formData.patientId && (
            <div className="income-section suggestions-section">
              {formData.status === TRANSACTION_STATUS.PENDING ? (
                // Pending: Show completed appointments without payments
                <>
                  <label className="income-section-label">
                    <CalendarBlank size={16} weight="duotone" />
                    פגישות ללא תשלום מקושר
                  </label>
                  {loadingAppointments ? (
                    <ContentLoader row size="xs" message="טוען פגישות..." />
                  ) : appointmentsWithoutPayments.length === 0 ? (
                    <div className="no-suggestions">
                      <CheckCircle size={18} weight="duotone" />
                      <span>לכל הפגישות יש תשלום מקושר</span>
                    </div>
                  ) : (
                    <div className="suggestions-list">
                      {appointmentsWithoutPayments.map((apt) => {
                        const isSelected = selectedLinkItem?.id === apt.id;
                        return (
                          <button
                            key={apt.id}
                            type="button"
                            className={`suggestion-item ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() =>
                              handleLinkItemSelect(apt, "appointment")
                            }
                          >
                            <div className="suggestion-check">
                              {isSelected && (
                                <CheckCircle size={16} weight="fill" />
                              )}
                            </div>
                            <div className="suggestion-info">
                              <span className="suggestion-date">
                                {formatDate(apt.startTime)}
                              </span>
                              <span className="suggestion-desc">פגישה</span>
                            </div>
                            <span className="suggestion-amount">
                              {formatCurrency(apt.paymentAmount)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                // Paid: Show pending debts
                <>
                  <label className="income-section-label">
                    <Clock size={16} weight="duotone" />
                    תשלומים ממתינים לסגירה
                  </label>
                  {loadingDebts ? (
                    <ContentLoader row size="xs" message="טוען תשלומים ממתינים..." />
                  ) : patientDebts.length === 0 ? (
                    <div className="no-suggestions">
                      <CheckCircle size={18} weight="duotone" />
                      <span>אין תשלומים ממתינים</span>
                    </div>
                  ) : (
                    <div className="suggestions-list">
                      {patientDebts.map((debt) => {
                        const isSelected = selectedLinkItem?.id === debt.id;
                        return (
                          <button
                            key={debt.id}
                            type="button"
                            className={`suggestion-item debt ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => handleLinkItemSelect(debt, "debt")}
                          >
                            <div className="suggestion-check">
                              {isSelected && (
                                <CheckCircle size={16} weight="fill" />
                              )}
                            </div>
                            <div className="suggestion-info">
                              <span className="suggestion-date">
                                {formatDate(debt.expectedPaymentDate)}
                              </span>
                              <span className="suggestion-desc">
                                {debt.description || "ממתין לתשלום"}
                              </span>
                            </div>
                            <span className="suggestion-amount">
                              {formatCurrency(debt.amount)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Link indicator */}
              {selectedLinkItem && (
                <div className="link-indicator">
                  <LinkSimple size={14} weight="bold" />
                  <span>
                    יקושר ל-
                    {selectedLinkItem.linkType === "appointment"
                      ? "פגישה"
                      : "תשלום ממתין"}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Amount */}
          <div className="income-section">
            <label className="income-section-label">סכום *</label>
            <div className="amount-input-wrapper">
              <span className="currency-symbol">₪</span>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="0"
                className="amount-input"
              />
            </div>

            {/* Amount mismatch warning */}
            {amountMismatch && (
              <div
                className={`amount-mismatch ${
                  amountMismatch.isUnderpayment ? "underpayment" : "overpayment"
                }`}
              >
                <WarningCircle size={16} weight="fill" />
                {amountMismatch.isUnderpayment ? (
                  <span>
                    סכום נמוך מהתשלום הממתין (
                    {formatCurrency(amountMismatch.linkAmount)}).
                    <strong> התשלום יפוצל</strong> - החלק ששולם יסומן והיתרה
                    תישאר ממתינה.
                  </span>
                ) : (
                  <span>
                    סכום גבוה מהתשלום הממתין (
                    {formatCurrency(amountMismatch.linkAmount)}). כל הסכום יירשם
                    כהכנסה.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Payment Method - Only for PAID status */}
          {formData.status === TRANSACTION_STATUS.PAID && (
            <div className="income-section">
              <label className="income-section-label">אמצעי תשלום</label>
              <div className="payment-method-grid">
                {PAYMENT_METHODS.map((method) => {
                  const MethodIcon = method.Icon;
                  const isSelected = formData.paymentMethod === method.value;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      className={`payment-method-btn ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          paymentMethod: method.value,
                        }))
                      }
                    >
                      <MethodIcon
                        size={20}
                        weight={isSelected ? "fill" : "duotone"}
                      />
                      <span>{method.label}</span>
                      {method.subLabel && <span style={{ fontSize: '0.55em', opacity: 0.6, display: 'block', lineHeight: 1.1 }}>{method.subLabel}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date */}
          <div className="income-section">
            <label className="income-section-label">
              {formData.status === TRANSACTION_STATUS.PAID
                ? "תאריך תשלום"
                : "תאריך צפוי"}
            </label>
            <DatePicker
              value={formData.paymentDate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentDate: e.target.value,
                }))
              }
              className="date-input"
            />
          </div>

          {/* Description */}
          <div className="income-section">
            <label className="income-section-label">תיאור</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="לדוגמא: טיפול, מכירת ציוד..."
              className="text-input"
            />
          </div>

          {/* Notes */}
          <div className="income-section">
            <label className="income-section-label">
              הערות
              <span className="optional-badge">אופציונלי</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="הערות נוספות..."
              rows={2}
              className="textarea-input"
            />
          </div>

          {blockedForOthers && (
            <div className="no-patient-warning" role="alert">
              <Warning size={16} weight="fill" />
              <span>
                {secretaryNeedsTarget
                  ? "יש לבחור מטופל/ת (או לקשר לתור) כדי שההכנסה תירשם על שם המטפל/ת שלו/ה"
                  : "אין לך הרשאה לרשום תשלום עבור תור של מטפל/ת אחר/ת"}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="income-modal-footer">
          <button type="button" className="cancel-btn" onClick={onClose}>
            ביטול
          </button>
          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={!formData.amount || isSubmitting || blockedForOthers}
          >
            {isSubmitting ? (
              <LoaderLoop size="xs" />
            ) : (
              <>
                {formData.status === TRANSACTION_STATUS.PAID ? (
                  <>
                    <CheckCircle size={18} weight="bold" />
                    רשום הכנסה
                  </>
                ) : (
                  <>
                    <Clock size={18} weight="bold" />
                    צור ממתין לתשלום
                  </>
                )}
              </>
            )}
          </button>
        </div>

        {/* Split Confirmation Dialog */}
        {showSplitConfirm && (
          <div className="split-confirm-overlay">
            <div className="split-confirm-dialog">
              <div className="split-confirm-icon">
                <ArrowsClockwise size={32} weight="duotone" />
              </div>
              <h3>פיצול תשלום</h3>
              <p>
                התשלום שהתקבל ({formatCurrency(formData.amount)}) נמוך מסכום
                התשלום הממתין ({formatCurrency(selectedLinkItem?.amount)}).
              </p>
              <p>
                <strong>מה יקרה:</strong>
                <br />• {formatCurrency(formData.amount)} יסומן כ"שולם"
                <br />•{" "}
                {formatCurrency(
                  amountMismatch?.linkAmount - parseFloat(formData.amount)
                )}{" "}
                יישאר כ"ממתין לתשלום"
              </p>
              <div className="split-confirm-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowSplitConfirm(false)}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={() => submitIncome(true)}
                >
                  <Lightning size={18} weight="bold" />
                  אישור פיצול
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default IncomeModal;
